import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Lottie from 'lottie-react';

import PagmusLogo from '../assets/logo/PagmuslogoNegativa.svg';
import EmailVerificationModal from "./modals/EmailVerificationModal";
import CPFValidator from "../utils/cpfValidation";
import PhoneValidator from "../utils/phoneValidation";
import AccountService from "../services/api/auth";
import LicenseService from "../services/LicenseService";
import useEmailValidation from "../hooks/useEmailValidation";
import useCPFValidation from "../hooks/useCPFValidation";
import { API_URL } from '../config/api';
import './SignUpLayer.css';

const SignUpLayer = ({ handleSignUp, loading }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [image] = useState("teste");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Estados para validação de senha
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false
  });
  
  // Estados para licença
  const [licenseCode, setLicenseCode] = useState("");
  const [isValidatingLicense, setIsValidatingLicense] = useState(false);
  const [licenseValidation, setLicenseValidation] = useState(null);
  
  // Hooks de validação em tempo real
  const { email: emailValue, setEmail: setEmailValue, isValidating: isValidatingEmail, validationResult: emailValidation } = useEmailValidation('');
  const { cpf: cpfValue, setCPF: setCPFValue, isValidating: isValidatingCPF, validationResult: cpfValidation } = useCPFValidation('');
  
  // Estados para modais
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  
  // Estados para verificação de email
  const [emailVerified, setEmailVerified] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verificationExpiresIn, setVerificationExpiresIn] = useState(600);
  
  // Estados para validação
  const [errors, setErrors] = useState({});
  // Estados de validação agora são gerenciados pelos hooks
  
  // Estado para animação Lottie
  const [lottieAnimation, setLottieAnimation] = useState(null);

  // Carregar animação Lottie
  useEffect(() => {
    let isMounted = true;
    
    const loadAnimation = async () => {
      try {
        const response = await fetch('/assets/animations/user-reviews.json');
        if (!response.ok) {
          throw new Error('Animação não encontrada');
        }
        const animationData = await response.json();
        if (isMounted) {
          setLottieAnimation(animationData);
        }
      } catch (error) {
        console.warn('Animação Lottie não disponível, usando fallback:', error);
        if (isMounted) {
          setLottieAnimation(null);
        }
      }
    };

    loadAnimation();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Validação em tempo real da senha
  useEffect(() => {
    const validatePassword = (pwd) => {
      setPasswordValidation({
        length: pwd.length >= 8,
        uppercase: /[A-Z]/.test(pwd),
        lowercase: /[a-z]/.test(pwd),
        number: /\d/.test(pwd),
        symbol: /[#@$!%*?&]/.test(pwd)
      });
    };

    if (password) {
      validatePassword(password);
    } else {
      setPasswordValidation({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        symbol: false
      });
    }
  }, [password]);

  // Validar licença em tempo real
  useEffect(() => {
    const validateLicense = async () => {
      if (!licenseCode || licenseCode.length < 8) {
        setLicenseValidation(null);
        return;
      }

      setIsValidatingLicense(true);
      try {
        const result = await LicenseService.validateLicense(licenseCode);
        setLicenseValidation(result);
      } catch (error) {
        setLicenseValidation({
          success: false,
          message: 'Erro ao validar licença',
          valid: false,
          canRegister: false
        });
      } finally {
        setIsValidatingLicense(false);
      }
    };

    const timeoutId = setTimeout(validateLicense, 500);
    return () => clearTimeout(timeoutId);
  }, [licenseCode]);

  // Validação de email agora é feita pelo hook useEmailValidation

  /**
   * Enviar código de verificação de email
   */
  const handleSendEmailVerification = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Digite um email válido');
      return;
    }

    setIsVerifyingEmail(true);

    try {
      const response = await fetch(`${API_URL}/api/email-verification/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          name: name || 'Novo usuário'
        })
      });

      const data = await response.json();

      if (data.success) {
        setVerificationExpiresIn(data.expiresIn || 600);
        setShowEmailVerificationModal(true);
        toast.success('Código enviado para seu email!');
      } else {
        toast.error(data.message || 'Erro ao enviar código');
      }
    } catch (error) {
      console.error('Erro ao enviar código:', error);
      toast.error('Erro ao enviar código de verificação');
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  /**
   * Verificar código de email
   */
  const handleVerifyEmailCode = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/email-verification/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          code
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailVerified(true);
        setShowEmailVerificationModal(false);
        toast.success('Email verificado com sucesso!');
        return { success: true };
      } else {
        return {
          success: false,
          message: data.message,
          attemptsLeft: data.attemptsLeft
        };
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      return {
        success: false,
        message: 'Erro ao verificar código'
      };
    }
  };

  /**
   * Reenviar código de verificação
   */
  const handleResendEmailCode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-verification/resend-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          name: name || 'Novo usuário'
        })
      });

      const data = await response.json();

      if (data.success) {
        setVerificationExpiresIn(data.expiresIn || 600);
        toast.success('Código reenviado!');
        return { success: true, expiresIn: data.expiresIn };
      } else {
        toast.error(data.message || 'Erro ao reenviar código');
        return { success: false };
      }
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
      toast.error('Erro ao reenviar código');
      return { success: false };
    }
  };

  // Formatação de CPF
  const handleCpfChange = (e) => {
    const value = e.target.value;
    const formatted = CPFValidator.formatCPF(value);
    setCpf(formatted);
    
    // Validação em tempo real
    if (formatted.length === 14) { // CPF completo com máscara
      const validation = CPFValidator.validateInput(formatted);
      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, cpf: validation.message }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.cpf;
          return newErrors;
        });
      }
    }
  };

  // Formatação de telefone
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = PhoneValidator.formatPhone(value);
    setPhone(formatted);
    
    // Validação em tempo real
    if (formatted.length === 15) { // Telefone completo com máscara
      const validation = PhoneValidator.validateInput(formatted);
      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, phone: validation.message }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phone;
          return newErrors;
        });
      }
    }
  };

  // Validação de campos obrigatórios
  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (emailValidation && !emailValidation.available) {
      newErrors.email = emailValidation.message;
    }

    if (!phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    } else if (phone.length < 15) {
      newErrors.phone = 'Telefone incompleto';
    }

    if (!cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (cpfValidation && !cpfValidation.valid) {
      newErrors.cpf = cpfValidation.message;
    } else if (cpfValidation && !cpfValidation.available) {
      newErrors.cpf = cpfValidation.message;
    }

    if (!password) {
      newErrors.password = 'Senha é obrigatória';
    } else {
      const validation = passwordValidation;
      if (!validation.length) {
        newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
      } else if (!validation.uppercase) {
        newErrors.password = 'Senha deve ter pelo menos 1 letra maiúscula';
      } else if (!validation.lowercase) {
        newErrors.password = 'Senha deve ter pelo menos 1 letra minúscula';
      } else if (!validation.number) {
        newErrors.password = 'Senha deve ter pelo menos 1 número';
      } else if (!validation.symbol) {
        newErrors.password = 'Senha deve ter pelo menos 1 símbolo (#@$!%*?&)';
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    if (!agreedToTerms) {
      newErrors.terms = 'Você deve concordar com os termos e condições';
    }

    // Validação da licença
    if (!licenseCode) {
      newErrors.license = 'Código da licença é obrigatório';
    } else if (licenseCode.length < 8) {
      newErrors.license = 'Código da licença deve ter 8 caracteres';
    } else if (!licenseValidation?.canRegister) {
      newErrors.license = 'Licença não autorizada';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submissão do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      await handleSignUp({ name, email, password, userImg: image, phone, cpf, licenseCode });
    } catch (error) {
      console.error('Erro no registro:', error);
      toast.error('Erro ao cadastrar usuário: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <>
      <div className="signup-v2-container">
        {/* Background com gradiente animado */}
        <div className="signup-v2-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        {/* Conteúdo principal */}
        <div className="signup-v2-content">
          {/* Lado esquerdo - Imagem/Animação */}
          <div className="signup-v2-left">
            <div className="signup-v2-hero">
              {/* Logo em Destaque */}
              <div className="brand-header">
                <img src={PagmusLogo} alt="Pagmus" className="brand-logo-main" />
              </div>

              <div className="hero-animation">
                {/* Animação Lottie de User Reviews */}
                <div className="lottie-animation-container">
                  {lottieAnimation ? (
                    <Lottie
                      animationData={lottieAnimation}
                      loop={true}
                      autoplay={true}
                      className="user-reviews-animation"
                    />
                  ) : (
                    <div className="loading-placeholder">
                      <Icon icon="mdi:loading" className="spinner" />
                      <span>Carregando animação...</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="hero-text">
                <span className="hero-title">Comece sua jornada digital hoje</span>
                <p>Junte-se a milhares de criadores que já transformaram suas paixões em negócios prósperos</p>
                
                <div className="stats-grid">
                  <div className="stat-item">
                    <strong>10k+</strong>
                    <span>Produtos</span>
                  </div>
                  <div className="stat-item">
                    <strong>50k+</strong>
                    <span>Usuários</span>
                  </div>
                  <div className="stat-item">
                    <strong>R$ 5M+</strong>
                    <span>Faturado</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lado direito - Formulário */}
          <div className="signup-v2-right">
            <div className="signup-v2-form-container">
              <div className="form-header">
                <div className="welcome-badge">
                  <Icon icon="mdi:account-plus" />
                  <span>Cadastro Gratuito</span>
                </div>
                <h2>Crie sua conta</h2>
                <p>Preencha seus dados e comece a vender em poucos minutos</p>
              </div>

              <form onSubmit={handleSubmit} className="signup-v2-form signup-form">
                {/* Nome */}
                <div className="form-group">
                  <label htmlFor="name">Nome completo</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="name"
                      placeholder="Seu nome completo"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.name;
                            return newErrors;
                          });
                        }
                      }}
                      className={errors.name ? 'error' : ''}
                      required
                    />
                  </div>
                  {errors.name && <div className="error-message">{errors.name}</div>}
                </div>

                {/* Email - PRIMEIRO PASSO */}
                <div className="form-group">
                  <label htmlFor="email">
                    E-mail {emailVerified && <Icon icon="mdi:check-circle" style={{ color: '#28a745', marginLeft: '5px' }} />}
                  </label>
                  <div className="input-wrapper" style={{ position: 'relative' }}>
                    <input
                      type="email"
                      id="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => {
                        if (!emailVerified) {
                          setEmail(e.target.value);
                          setEmailValue(e.target.value);
                        }
                      }}
                      disabled={emailVerified}
                      style={{
                        borderColor: emailVerified ? '#28a745' : 
                                   (emailValidation?.available === false ? '#dc3545' : ''),
                        paddingRight: '120px',
                        background: emailVerified ? '#f0f9ff' : 'white',
                      }}
                      required
                    />
                    {!emailVerified && (
                      <button
                        type="button"
                        onClick={handleSendEmailVerification}
                        disabled={isVerifyingEmail || !email.includes('@') || emailValidation?.available === false}
                        style={{
                          position: 'absolute',
                          right: '5px',
                          ...(!(isVerifyingEmail || !email || !email.includes('@') || emailValidation?.available === false) && {
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }),
                          padding: '8px 16px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                          zIndex: 10,
                          opacity: (isVerifyingEmail || !email || !email.includes('@') || emailValidation?.available === false) ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.transform = 'translateY(-50%) scale(1.05)';
                            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.transform = 'translateY(-50%) scale(1)';
                            e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                          }
                        }}
                      >
                        {isVerifyingEmail ? 'Enviando...' : 'Validar'}
                      </button>
                    )}
                  </div>
                  {emailVerified && (
                    <div className="validation-message success">
                      <Icon icon="mdi:check-circle" style={{ marginRight: '5px' }} />
                      Email verificado com sucesso!
                    </div>
                  )}
                  {!emailVerified && emailValidation && emailValidation.available === false && (
                    <div className="validation-message error">
                      {emailValidation.message}
                    </div>
                  )}
                </div>

                {/* Telefone e CPF em linha - DESABILITADO ATÉ EMAIL VERIFICADO */}
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label htmlFor="phone">Telefone</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="phone"
                        placeholder="(00) 00000-0000"
                        value={phone}
                        onChange={handlePhoneChange}
                        className={`${errors.phone ? 'error' : ''} ${!emailVerified ? 'disabled-input' : ''}`}
                        disabled={!emailVerified}
                        required
                      />
                    </div>
                    {errors.phone && <div className="error-message">{errors.phone}</div>}
                  </div>

                  <div className="form-group form-group-half">
                    <label htmlFor="cpf">CPF</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="cpf"
                        placeholder="000.000.000-00"
                        value={cpf}
                        onChange={(e) => {
                          handleCpfChange(e);
                          setCPFValue(e.target.value);
                        }}
                        className={!emailVerified ? 'disabled-input' : ''}
                        style={{
                          borderColor: cpfValidation?.available ? '#28a745' :
                                     cpfValidation?.available === false ? '#dc3545' : ''
                        }}
                        disabled={!emailVerified}
                        required
                      />
                      {isValidatingCPF && (
                        <div className="spinner-wrapper">
                          <div className="spinner"></div>
                        </div>
                      )}
                      {cpfValidation?.available && (
                        <Icon icon="mdi:check-circle" className="validation-icon success" />
                      )}
                      {cpfValidation?.available === false && (
                        <Icon icon="mdi:alert-circle" className="validation-icon error" />
                      )}
                    </div>
                    {cpfValidation && (
                      <div className={`validation-message ${cpfValidation.available ? 'success' : 'error'}`}>
                        {cpfValidation.message}
                      </div>
                    )}
                    {errors.cpf && <div className="error-message">{errors.cpf}</div>}
                  </div>
                </div>

                {/* Senha e Confirmação em duas colunas */}
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label htmlFor="password">Senha</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Digite a senha"
                        value={password}
                        disabled={!emailVerified}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errors.password) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.password;
                              return newErrors;
                            });
                          }
                        }}
                        className={`${errors.password ? 'error' : ''} ${!emailVerified ? 'disabled-input' : ''}`}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={!emailVerified}
                      >
                        <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} />
                      </button>
                    </div>
                    {errors.password && <div className="error-message">{errors.password}</div>}
                  </div>

                  <div className="form-group form-group-half">
                    <label htmlFor="confirmPassword">Confirmar Senha</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="confirmPassword"
                        placeholder="Confirme senha"
                        value={confirmPassword}
                        disabled={!emailVerified}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.confirmPassword;
                              return newErrors;
                            });
                          }
                        }}
                        className={`${errors.confirmPassword ? 'error' : ''} ${!emailVerified ? 'disabled-input' : ''}`}
                        required
                      />
                    </div>
                    {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                  </div>
                </div>
                
                {/* Indicador de validação de senha */}
                <div className="password-validation">
                  <span className="validation-title">Critérios da senha:</span>
                  <div className="validation-criteria">
                    <div className={`criterion ${passwordValidation.length ? 'valid' : 'invalid'}`}>
                      <Icon icon={passwordValidation.length ? "mdi:check-circle" : "mdi:circle-outline"} />
                      <span>Pelo menos 8 caracteres</span>
                    </div>
                    <div className={`criterion ${passwordValidation.uppercase ? 'valid' : 'invalid'}`}>
                      <Icon icon={passwordValidation.uppercase ? "mdi:check-circle" : "mdi:circle-outline"} />
                      <span>1 letra maiúscula (A-Z)</span>
                    </div>
                    <div className={`criterion ${passwordValidation.lowercase ? 'valid' : 'invalid'}`}>
                      <Icon icon={passwordValidation.lowercase ? "mdi:check-circle" : "mdi:circle-outline"} />
                      <span>1 letra minúscula (a-z)</span>
                    </div>
                    <div className={`criterion ${passwordValidation.number ? 'valid' : 'invalid'}`}>
                      <Icon icon={passwordValidation.number ? "mdi:check-circle" : "mdi:circle-outline"} />
                      <span>1 número (0-9)</span>
                    </div>
                    <div className={`criterion ${passwordValidation.symbol ? 'valid' : 'invalid'}`}>
                      <Icon icon={passwordValidation.symbol ? "mdi:check-circle" : "mdi:circle-outline"} />
                      <span>1 símbolo (#@$!%*?&)</span>
                    </div>
                  </div>
                </div>

                {/* Código da Licença */}
                <div className="form-group">
                  <label htmlFor="licenseCode">Código da Licença</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="licenseCode"
                      placeholder="Digite o código da licença (8 caracteres)"
                      value={licenseCode}
                      disabled={!emailVerified}
                      onChange={(e) => setLicenseCode(e.target.value.toUpperCase())}
                      maxLength={8}
                      className={!emailVerified ? 'disabled-input' : ''}
                      style={{
                        borderColor: licenseValidation?.success ? '#28a745' : 
                                   licenseValidation?.success === false ? '#dc3545' : ''
                      }}
                    />
                    {isValidatingLicense && (
                      <div className="spinner-wrapper">
                        <div className="spinner"></div>
                      </div>
                    )}
                    {licenseValidation?.success && (
                      <Icon icon="mdi:check-circle" className="validation-icon success" />
                    )}
                    {licenseValidation?.success === false && (
                      <Icon icon="mdi:alert-circle" className="validation-icon error" />
                    )}
                  </div>
                  {licenseValidation && (
                    <div className={`validation-message ${licenseValidation.success ? 'success' : 'error'}`}>
                      {licenseValidation.message}
                    </div>
                  )}
                  {errors.license && <div className="error-message">{errors.license}</div>}
                </div>

                {/* Termos e Condições */}
                <div className="form-group">
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => {
                        setAgreedToTerms(e.target.checked);
                        if (errors.terms) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.terms;
                            return newErrors;
                          });
                        }
                      }}
                    />
                    <span className="checkmark"></span>
                    <span className="checkbox-text">
                      Concordo com os{" "}
                      <a
                        href="/legal/termos-de-uso.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-button"
                      >
                        Termos de Uso
                      </a>{" "}
                      e{" "}
                      <a
                        href="/legal/politica-de-privacidade.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-button"
                      >
                        Política de Privacidade
                      </a>
                    </span>
                  </label>
                  {errors.terms && <div className="error-message">{errors.terms}</div>}
                </div>

                {/* Botão de Cadastro */}
                <button
                  type="submit"
                  className={`signup-btn ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Icon icon="mdi:loading" className="spinner" />
                      Criando conta...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:account-plus" />
                      Criar conta gratuita
                    </>
                  )}
                </button>

                {/* Link para login */}
                <div className="signin-link">
                  <p>
                    Já possui uma conta?{" "}
                    <Link to="/login">Entrar agora</Link>
                  </p>
                </div>
              </form>

              {/* Trust indicators */}
              <div className="trust-indicators">
                <div className="trust-item">
                  <Icon icon="mdi:shield-check" />
                  <span>Dados Protegidos</span>
                </div>
                <div className="trust-item">
                  <Icon icon="mdi:flash" />
                  <span>Setup Rápido</span>
                </div>
                <div className="trust-item">
                  <Icon icon="mdi:gift" />
                  <span>Sempre Gratuito</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de verificação de email */}
      <EmailVerificationModal
        show={showEmailVerificationModal}
        onHide={() => setShowEmailVerificationModal(false)}
        onVerify={handleVerifyEmailCode}
        onResend={handleResendEmailCode}
        email={email}
        name={name || 'Novo usuário'}
        expiresIn={verificationExpiresIn}
      />
    </>
  );
};

export default SignUpLayer;
